import { oneWay } from '@ember/object/computed';
import Route from 'cio/routes/base';
import { action } from '@ember/object';
import Transition from '@ember/routing/-private/transition';
import PersonController from 'cio/pods/person/controller';
import CustomerModel from 'cio/models/customer';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';

export default class PersonRoute extends Route {
  @service router!: RouterService;
  @service store!: DS.Store;

  crumb() {
    return {
      name: oneWay('model.emailOrId') as unknown as string,
      path: 'person.index',
      model: this.modelFor('person'),
    };
  }

  async model(params: Record<string, any>) {
    // params.person_id can be either external ID (customer.id) or internal ID (customer.cio_id)
    //
    // Ember Data since 4.8 doesn't like the fact that .findRecord() can be called with
    // either, but back-end always returns record with external "id".
    //
    // We need to do some gymnastics to handle both cases - if a customer is
    // already loaded, let's return it instead of calling "findRecord()" again -
    // possibly with other ID type (external/internal), which can error out the
    // adapter.
    //
    // A test showing the issue: http://localhost:4200/tests?testId=d5c382a6
    let { person_id: id } = params;

    let existingCustomer = this.store.peekAll('customer').find((c) => Object.values(c.identifiers).includes(id));
    if (existingCustomer != null) {
      await existingCustomer.reload(); // just to be safe
      return existingCustomer;
    }

    return this.store.findRecord('customer', id);
  }

  // Currently users access person pages using the external id of a customer for the id param.
  // In the future, however, person pages will use the internal id as the id param. If they access a
  // person page by the external id because it was bookmarked or a link was shared, then we should
  // redirect them to the person page using the internal id instead.
  redirect(model: CustomerModel, transition: Transition) {
    let params = this.paramsFor('person') as Record<string, string>;

    if (params.person_id !== model.id) {
      // We need to construct an array of all param IDs required for the target
      // route, keeping in mind that the target route may be a nested child.
      // For a more specific example, a journey child route needs both the
      // person id and the journey id passed in. We need to replace the person
      // id with the internal one, but keep the journey ID as is.

      // Filter out person_id, if it exists (inspecting the RouteInfo objects
      // shows me that, for some reason, person_id isn't included in the params
      // array. I'm guessing that's because of resetNamespace.
      // Just in case, we'll filter it out (we're always adding the person ID at
      // the start anyway).
      let targetParamValues = transition.to?.params ?? {};
      let targetParamNames = (transition.to?.paramNames ?? []).filter((p) => p !== 'person_id');
      let paramValues = [model.id, ...targetParamNames.map((paramName) => targetParamValues[paramName])].compact();

      // @ts-ignore transitionTo typing doesn't support destructured argument
      this.router.transitionTo(transition.to.name, ...paramValues);
    }
  }

  setupController(controller: PersonController, model: CustomerModel, transition: Transition) {
    super.setupController(controller, model, transition);

    controller.refreshAttributesIfNecessary.perform();
  }

  resetController(controller: PersonController, isExiting: boolean, transition: Transition) {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      controller.refreshAttributesIfNecessary.cancelAll();
    }
  }

  @action
  refreshModel() {
    return this.refresh();
  }
}
